import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Button, Badge, Form, Alert } from 'react-bootstrap';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import { DeleteModal } from '../../../Common/DeleteModal';
import DetailSection from '../../../Common/DetailSection';
import FeatureFormModal from './FeatureFormModal';
import { getImageUrl } from '../../../utils/imageUrl';
import { hasAnyPermission, type User } from '../../../utils/roleBasedAccess';
import {
  getFeature,
  deleteFeature,
  createOrUpdateFeature,
  Feature
} from '../../../api/featureManagement/featureService';
import { displayConditionValue } from '../conditionUtils';
import '../FeatureManagement.css';
import './FeatureDetailPage.css';

const FeatureDetailPage: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const userData: User = useMemo(
    () => ({
      ...(user || {}),
      subRole: (user && user.subRole) || role,
      permissions: permissions,
    }),
    [user, role, permissions]
  );
  const hasManagePermissions = hasAnyPermission(userData, [
    'feature_management.admin',
    'feature_management.features.manage',
  ]);
  const baseClientId = process.env.REACT_APP_CLIENT_ID || user?.clientId || '';
  
  const locationState = location.state as any;
  const clientIdFromState = locationState?.clientId || locationState?.feature?.clientId;
  const clientId = clientIdFromState || baseClientId;
  const scope = locationState?.scope || 'tournament';
  const isEditMode = locationState?.edit || false;

  const [feature, setFeature] = useState<Feature | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showKillModal, setShowKillModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isKilling, setIsKilling] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [killError, setKillError] = useState<string | null>(null);

  useEffect(() => {
    if (name) {
      loadFeature();
    }
  }, [name, clientId, scope]);

  const loadFeature = async () => {
    if (!name) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getFeature({ name, clientId, scope });
      setFeature(data);
    } catch (err: any) {
      console.error('Error loading feature:', err);
      setError(err.message || 'Failed to load feature. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/feature-management/features');
  };

  const handleEdit = () => {
    if (!feature) return;
    if (!hasManagePermissions) return;
    setShowFormModal(true);
  };

  const handleDelete = () => {
    if (!hasManagePermissions) return;
    setShowDeleteModal(true);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!feature || !name) return;
    if (!hasManagePermissions) return;

    try {
      setIsDeleting(true);
      setDeleteError(null);
      await deleteFeature({ name, clientId, scope });
      navigate('/feature-management/features');
    } catch (err: any) {
      console.error('Error deleting feature:', err);
      setDeleteError(err.message || 'Failed to delete feature. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleKill = () => {
    if (!hasManagePermissions) return;
    setShowKillModal(true);
    setKillError(null);
  };

  const confirmKill = async () => {
    if (!feature || !name) return;
    if (!hasManagePermissions) return;

    try {
      setIsKilling(true);
      setKillError(null);
      await createOrUpdateFeature({
        name: feature.name,
        scope: feature.scope,
        status: 'KILLED',
        clientId,
        conditions: feature.conditions,
        setConditions: !!(feature.conditions && feature.conditions.length > 0)
      });
      await loadFeature();
      setShowKillModal(false);
    } catch (err: any) {
      console.error('Error killing feature:', err);
      setKillError(err.message || 'Failed to kill feature. Please try again.');
    } finally {
      setIsKilling(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!feature || !name) return;
    if (!hasManagePermissions) return;

    try {
      setIsToggling(true);
      const newStatus = feature.status === 'ENABLED' ? 'DISABLED' : 'ENABLED';
      await createOrUpdateFeature({
        name: feature.name,
        scope: feature.scope,
        status: newStatus,
        clientId: feature.clientId,
        conditions: feature.conditions,
        setConditions: !!(feature.conditions && feature.conditions.length > 0)
      });
      await loadFeature();
    } catch (err: any) {
      console.error('Error toggling feature status:', err);
      setError(err.message || 'Failed to update feature status. Please try again.');
    } finally {
      setIsToggling(false);
    }
  };

  const handleRestore = async () => {
    if (!feature || !name) return;
    if (!hasManagePermissions) return;

    try {
      setIsRestoring(true);
      await createOrUpdateFeature({
        name: feature.name,
        scope: feature.scope,
        status: 'ENABLED',
        clientId,
        conditions: feature.conditions,
        setConditions: !!(feature.conditions && feature.conditions.length > 0)
      });
      await loadFeature();
    } catch (err: any) {
      console.error('Error restoring feature:', err);
      setError(err.message || 'Failed to restore feature. Please try again.');
    } finally {
      setIsRestoring(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ENABLED':
        return { label: 'Enabled', variant: 'success' as const };
      case 'DISABLED':
        return { label: 'Disabled', variant: 'secondary' as const };
      case 'KILLED':
        return { label: 'This Feature is Killed', variant: 'danger' as const };
      default:
        return { label: status, variant: 'secondary' as const };
    }
  };

  if (loading) {
    return (
      <Container fluid className="feature-management-page">
        <ShimmerLoader type="table" rows={3} />
      </Container>
    );
  }

  if (error && !feature) {
    return (
<Container fluid className="feature-management-page">
      <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={handleBack}>
            Go Back
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!feature) {
    return (
<Container fluid className="feature-management-page">
      <Alert variant="warning">
          <Alert.Heading>Feature Not Found</Alert.Heading>
          <p>The feature you're looking for doesn't exist.</p>
          <Button variant="outline-warning" onClick={handleBack}>
            Go Back
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="feature-management-page">
      {/* Breadcrumbs */}
      <div className="mb-3 mt-4" style={{ padding: '0 15px' }}>
        <Breadcrumbs
          items={[
            { label: 'Feature Management', onClick: () => navigate('/feature-management') },
            { label: 'Features', onClick: () => navigate('/feature-management/features') },
            { label: feature.name, active: true }
          ]}
        />
      </div>

      {/* Header */}
      <div style={{ padding: '0 15px', marginBottom: '24px' }}>
        <Button
          variant="link"
          onClick={handleBack}
          className="mb-2 p-0"
          style={{ textDecoration: 'none', color: '#212529' }}
        >
          ← Back to Features
        </Button>
        <h2 className="mb-0">{feature.name}</h2>
      </div>

      {/* Error Alert */}
      {error && (
        <div style={{ padding: '0 15px', marginBottom: '16px' }}>
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      {/* Details Section */}
      <div style={{ padding: '0 15px' }}>
        <DetailSection
          title="Details"
          status={
            feature.status === 'ENABLED' || feature.status === 'KILLED'
              ? getStatusBadge(feature.status)
              : undefined
          }
          fields={[
            { label: 'Feature Name', value: feature.name },
            { label: 'Scope', value: feature.scope },
            { label: 'Client ID', value: feature.clientId },
            { label: 'Status', value: feature.status }
          ]}
          actions={
            <div className="d-flex align-items-center" style={{ gap: '12px' }}>
              <Form.Check
                type="switch"
                id="status-switch"
                className="feature-status-switch"
                label={feature.status === 'ENABLED' ? 'Enabled' : 'Disabled'}
                checked={feature.status === 'ENABLED'}
                onChange={handleToggleStatus}
                disabled={!hasManagePermissions || isToggling || feature.status === 'KILLED'}
              />
              {hasManagePermissions && feature.status !== 'KILLED' && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="kill-feature-btn"
                  onClick={handleKill}
                  disabled={isKilling}
                >
                  <span className="kill-feature-btn-label">Kill the Feature</span>
                  <img
                    src={getImageUrl('shared/assets/icons/forbidden_circle.svg')}
                    alt="Kill feature"
                    style={{ width: 14, height: 14 }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const fallbackIcon = document.createElement('i');
                      fallbackIcon.className = 'ph-prohibit-circle';
                      target.parentElement?.appendChild(fallbackIcon);
                    }}
                  />
                </Button>
              )}
              {hasManagePermissions && feature.status === 'KILLED' && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="restore-feature-btn"
                  onClick={handleRestore}
                  disabled={isRestoring}
                >
                  {isRestoring ? 'Restoring...' : 'Restore'}
                  {!isRestoring && (
                  <img
                    src={getImageUrl('shared/assets/icons/export_refresh.svg')}
                    alt="Restore"
                    style={{ width: 14, height: 14 }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const fallbackIcon = document.createElement('i');
                      fallbackIcon.className = 'ph-arrows-clockwise';
                      target.parentElement?.appendChild(fallbackIcon);
                    }}
                  />
                )}
                </Button>
              )}
              {hasManagePermissions && (
                <>
                  <Button
                    variant="dark"
                    size="sm"
                    className="edit-feature-btn"
                    onClick={handleEdit}
                  >
                    Edit
                    <img
                      src={getImageUrl('shared/assets/icons/edit_white.svg')}
                      alt="Edit"
                      style={{ width: 14, height: 14 }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallbackIcon = document.createElement('i');
                        fallbackIcon.className = 'ph-pencil-simple';
                        target.parentElement?.appendChild(fallbackIcon);
                      }}
                    />
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    className="delete-feature-btn"
                    onClick={handleDelete}
                  >
                    Delete
                    <img
                      src={getImageUrl('shared/assets/icons/trash_white.svg')}
                      alt="Delete"
                      style={{ width: 14, height: 14 }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallbackIcon = document.createElement('i');
                        fallbackIcon.className = 'ph-trash';
                        target.parentElement?.appendChild(fallbackIcon);
                      }}
                    />
                  </Button>
                </>
              )}
            </div>
          }
          conditions={
            feature.conditions && feature.conditions.length > 0
              ? {
                  title: 'Conditions',
                  items: feature.conditions.map((cond) => ({
                    name: cond.name,
                    value: `${cond.comparator} ${displayConditionValue(cond.value)}`
                  }))
                }
              : undefined
          }
        />
      </div>

      {/* Kill Modal */}
      <DeleteModal
        show={showKillModal}
        handleClose={() => {
          setShowKillModal(false);
          setKillError(null);
        }}
        deleteModalFunction={confirmKill}
        title="Kill This Feature?"
        message="Once Killed, this feature will no longer be available to users unless an override is applied. You can Restore it again later."
        confirmLabel="Kill Feature"
        cancelLabel="Go Back"
        isLoading={isKilling}
        error={killError}
      />

      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={() => {
          setShowDeleteModal(false);
          setDeleteError(null);
        }}
        deleteModalFunction={confirmDelete}
        title="Delete This Feature?"
        message="Once deleted, this feature will be permanently removed. This action cannot be undone."
        confirmLabel="Confirm Delete"
        cancelLabel="Go Back"
        isLoading={isDeleting}
        error={deleteError}
      />

      {/* Feature Form Modal (Edit Feature) */}
      <FeatureFormModal
        show={showFormModal}
        handleClose={() => setShowFormModal(false)}
        onSuccess={async () => {
          setShowFormModal(false);
          await loadFeature();
        }}
        feature={feature}
        clientId={clientId}
        scope={scope}
      />
    </Container>
  );
};

export default FeatureDetailPage;
